import { memo, useEffect, useRef } from "react";
import { APP_NAME, installAppLabel } from "../../../config/brand";
import { C } from "../../match/shared/matchTokens";

/**
 * Platform-specific install steps when the browser cannot show a native prompt.
 * @param {{ open: boolean, iosHint: boolean, onClose: () => void }} props
 */
function PwaInstallSheet({ open, iosHint, onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="pwa-install-sheet-backdrop"
      role="presentation"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(7,11,38,0.72)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        padding: "16px 16px calc(16px + env(safe-area-inset-bottom))",
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-install-sheet-title"
        className="pwa-install-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(480px, 100%)",
          background: "#111a44",
          border: `1px solid ${C.bdr}`,
          borderRadius: "20px 20px 16px 16px",
          padding: "20px 18px 18px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
          fontFamily: "var(--tra-font-family-body)",
        }}
      >
        <h2
          id="pwa-install-sheet-title"
          style={{
            margin: "0 0 12px",
            fontFamily: "var(--tra-font-family-display)",
            fontSize: 18,
            fontWeight: 800,
            color: "#fff",
          }}
        >
          {installAppLabel()}
        </h2>
        {iosHint ? (
          <ol
            style={{
              margin: "0 0 16px",
              paddingLeft: 20,
              color: C.lav,
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <li style={{ marginBottom: 8 }}>
              Tap <strong>Share</strong> in the Safari toolbar (square with arrow).
            </li>
            <li style={{ marginBottom: 8 }}>
              Scroll and tap <strong>Add to Home Screen</strong>.
            </li>
            <li>
              Tap <strong>Add</strong> — {APP_NAME} opens full screen like an app.
            </li>
          </ol>
        ) : (
          <ol
            style={{
              margin: "0 0 16px",
              paddingLeft: 20,
              color: C.lav,
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <li style={{ marginBottom: 8 }}>
              Open the browser menu <strong>(⋮)</strong> or look for an{" "}
              <strong>Install</strong> icon in the address bar.
            </li>
            <li style={{ marginBottom: 8 }}>
              Choose <strong>Install app</strong> or <strong>Add to Home screen</strong>.
            </li>
            <li>Confirm — the app will appear on your home screen.</li>
          </ol>
        )}
        <button
          ref={closeRef}
          type="button"
          className="lobby-submit"
          onClick={onClose}
          style={{ marginTop: 0, width: "100%" }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

export default memo(PwaInstallSheet);
